import React from "react";
import { Link } from "react-router-dom";
import { useSeo, faqJsonLd } from "@/lib/seo";
import { PageHeader } from "@/components/shared";
import ScopeOfPractice from "@/components/ScopeOfPractice";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  { q: "Is this a form of therapy?", a: "It's a complementary, experiential modality that works alongside — not instead of — therapy or medical care. Where talk therapy explores your story, constellation work reveals the systemic dynamic underneath it." },
  { q: "Is it evidence-based or religious?", a: "It is neither religious nor mystical. It's a structured, embodied method. We're honest about what can and cannot be claimed: many people report meaningful shifts, and we never promise cures or guaranteed outcomes." },
  { q: "Will I have to perform emotionally in front of strangers?", a: "No. In individual work there is no audience at all. In group work you choose your level of involvement, and you never have to disclose private family details publicly." },
];

export default function WhatIsIt() {
  useSeo({
    title: "What Is Family & Systemic Constellation Work?",
    description: "A plain-language guide to Family & Systemic Constellation work — what it is, what it isn't, and how it reaches patterns talking alone cannot. Grounded and non-mystical.",
    path: "/what-is-family-constellation",
    type: "article",
    jsonLd: faqJsonLd(faqs),
  });
  return (
    <div>
      <PageHeader
        crumb="What Is It"
        title="What is Family & Systemic Constellation work?"
        intro="A plain-language explanation of the method — what it is, what it isn't, and why it can reach places talking alone cannot."
      />

      <section className="container-page pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8 prose-editorial text-earth/85">
            <p className="text-xl leading-relaxed">
              Most of us are raised to think of ourselves as individuals. But we are also part of something larger — a family
              system that stretches back generations, with its own loyalties, losses, and unspoken rules. Family Constellation
              work looks at a difficulty through the lens of that whole system, rather than the person alone.
            </p>

            <h2>Why the system matters</h2>
            <p>
              Often a struggle we carry — a repeating conflict, an anxiety, a block around success or love — isn't only ours.
              It can belong to the system: an earlier loss that was never grieved, someone excluded, a burden silently passed
              down. Out of a deep, unconscious loyalty, we sometimes carry what was never ours to carry.
            </p>

            <h2>What actually happens</h2>
            <p>
              In a constellation, we represent the elements of your system spatially. In a group, other participants stand in
              as representatives; in individual work, we use figures or anchors on the floor. Something remarkable then
              happens: the arrangement begins to reveal the hidden dynamic, and — gently — a movement toward resolution.
            </p>
            <blockquote>It is experiential, not analytical. You don't think your way through it. You witness it, and something settles.</blockquote>

            <h2>What it is not</h2>
            <p>
              It is not fortune-telling, not a belief system, and not a replacement for medical or psychiatric care. It does
              not require you to relive trauma in detail, and it makes no guarantees. It is a quiet, respectful way of letting
              a system find its own order again.
            </p>

            <h2>Who it tends to help</h2>
            <ul>
              <li>People who feel they keep repeating a pattern they can't explain.</li>
              <li>Those carrying grief, anxiety, or a sense of burden that feels "older" than their own life.</li>
              <li>Anyone facing a family knot that talking alone hasn't loosened.</li>
            </ul>

            <div className="not-prose my-10">
              <ScopeOfPractice />
            </div>

            <h2>Common questions</h2>
            <div className="not-prose">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b hairline">
                    <AccordionTrigger className="text-left text-lg text-earth hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-earth/75 text-base pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-surface-card border hairline rounded-lg p-7">
              <h3 className="text-xl text-earth mb-2">Curious to experience it?</h3>
              <p className="text-earth/70 mb-5">The gentlest place to begin is a first individual session or an introductory workshop.</p>
              <div className="flex flex-col gap-3">
                <Link to="/book" className="btn-gold w-full">Book a Session</Link>
                <Link to="/how-it-works" className="btn-outline w-full">See the process</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
